import { PostEditorReducerFunction, PostEditorUpdateCustomizeProfilesAction } from '../PostEditorActions';
import {
  GroupPostAttributesType,
  PostEditorContextEnum,
  PostEditorState,
  ReadonlyGroupPostAttributesType
} from '../../../../models/post-editor';
import { ImmutabilityContext } from '../effects/customImmutabilityContext';
import { mapPostToGroupPostAttributesType } from '../../helpers/mapFetchedPostsToState';
import { PostSource, PostStatus } from '../../../../models/graphql';
import { showToast } from '../../../../helpers/showToast';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { InitialPostEditorState } from '../initial';
import { validatorStateUpdater } from '../../validators/helpers/validatorStateUpdater';

export const updateCustomizeProfilesReducer: PostEditorReducerFunction<
  PostEditorUpdateCustomizeProfilesAction['payload']
> = (state, payload) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, availableProfiles, siblings, postEditorContext } = payload;
  const initialGroup = InitialPostEditorState().postGroups[0].groupPostAttributes;
  const {
    content: initialContent,
    scheduling: initialScheduling,
    postSource: initialPostSource,
    parentId: initialParentId
  } = state.postGroups[postGroupIndex].groupPostAttributes;
  // ^if we are adding new profiles in create mode, we always want to initialize it with empty state.
  // if we are in edit mode, and we add new, we'll copy data from original post (line 51, mapped state)
  const customizeContentPerProfile = state.postGroups[postGroupIndex].customizeContentPerProfile;
  const customizeSchedulePerProfile = state.postGroups[postGroupIndex].customizeSchedulePerProfile;
  let result = updateImmutable(state, {});
  if (customizeContentPerProfile || customizeSchedulePerProfile) {
    //check if adding or removing a profile
    const profilesIds = state.postGroups[postGroupIndex].profiles.map(profile => profile.profileId);
    const postsProfileIds = state.postGroups[postGroupIndex].posts.map(post => post.customizedProfileId);
    const readonlyPostsProfileIds = state.postGroups[postGroupIndex].readOnlyPosts.map(
      post => post.customizedProfileId
    );

    if (profilesIds.length !== postsProfileIds.length) {
      result = updateImmutable(state, {
        postGroups: {
          [postGroupIndex]: {
            activeCustomizeIndex: { $set: null }
          }
        }
      });
    }
    // ^ if we add or remove, collapse accordion
    if (profilesIds.length > postsProfileIds.length) {
      // adding profile(s)
      profilesIds.forEach((profile, profileIndex) => {
        const profileId = postsProfileIds.find(value => value === profile);
        if (!profileId) {
          const readonlyProfileIndex = readonlyPostsProfileIds.findIndex(itemId => itemId === profile);
          let postAttributes: Readonly<GroupPostAttributesType> = initialGroup;
          // add new profile
          const mappedState = siblings ? siblings.find(postItem => postItem.socialProfile.id === profile) : undefined;
          if (mappedState) {
            postAttributes = mapPostToGroupPostAttributesType(
              mappedState,
              {
                ...result.postGroups[postGroupIndex].groupPostAttributes,
                customizedProfileId:
                  mappedState.socialProfile.id ||
                  result.postGroups[postGroupIndex].groupPostAttributes.customizedProfileId
              },
              postEditorContext.type
            );
          } else {
            postAttributes = updateImmutable(postAttributes, {
              postSource: { $set: initialPostSource },
              parentId: { $set: initialParentId }
            });
            if (customizeContentPerProfile && postEditorContext.type === PostEditorContextEnum.edit) {
              postAttributes = updateImmutable(postAttributes, {
                content: {
                  $set: initialContent
                }
              });
            }
            if (customizeSchedulePerProfile && postEditorContext.type === PostEditorContextEnum.edit) {
              postAttributes = updateImmutable(postAttributes, {
                scheduling: {
                  $set: initialScheduling
                }
              });
            }
          }
          const selectedProfile = availableProfiles.find(p => p.id === profile);
          const currentPostStatus = postAttributes.postStatus;
          postAttributes = updateImmutable<GroupPostAttributesType>(postAttributes, {
            customizedProfileId: {
              $set: profile
            },
            postStatus: {
              $apply: current =>
                current === PostStatus.PUBLISHED || current === PostStatus.FAILED ? PostStatus.ACTIVE : current
            },
            postId: {
              $set:
                siblings?.find(
                  post =>
                    post.socialProfile &&
                    post.socialProfile.id === selectedProfile?.id &&
                    post.status !== PostStatus.PUBLISHED
                )?.id || null
            },
            postSource: {
              // in case we have post status as Published it means that we are rescheduling a new post,
              // if statrus is Failed it means we are editing a failed post so we don't even need postsource
              // and in any other case, we should already have the postSource set.
              $apply: current => {
                switch (currentPostStatus) {
                  case PostStatus.FAILED:
                    return null;
                  case PostStatus.PUBLISHED:
                    return PostSource.RESCHEDULE_SIBLING_FROM_EDIT;
                  default:
                    return current ?? PostSource.ADD_SIBLING_FROM_EDIT;
                }
              }
            }
          });
          result = updateImmutable(result, {
            postGroups: {
              [postGroupIndex]: {
                posts: {
                  $splice: [[profileIndex, 0, postAttributes]]
                },
                readOnlyPosts: {
                  $apply: (current: ReadonlyGroupPostAttributesType[]) =>
                    readonlyProfileIndex > -1
                      ? current.map((p, pIndex) => (pIndex === readonlyProfileIndex ? { ...p, active: false } : p))
                      : current
                }
              }
            }
          });
        }
      });
    } else if (profilesIds.length < postsProfileIds.length && profilesIds.length) {
      // we still have profiles, we didn't click to deselect all, or we still have some selected profiles
      // const selectedProfiles = availableProfiles.filter(p => profilesIds.includes(p.id));
      postsProfileIds.forEach((profile, profileIndex) => {
        const profileIdx = profilesIds.findIndex(value => value === profile);
        const readonlyProfileIndex = readonlyPostsProfileIds.findIndex(itemId => itemId === profile);
        if (profileIdx < 0) {
          // it doesn't exist, remove it from the list
          result = updateImmutable(result, {
            postGroups: {
              [postGroupIndex]: {
                posts: {
                  $splice: [[profileIndex, 1]]
                },
                readOnlyPosts: {
                  $apply: (current: ReadonlyGroupPostAttributesType[]) =>
                    readonlyProfileIndex > -1
                      ? current.map((p, pIndex) => (pIndex === readonlyProfileIndex ? { ...p, active: true } : p))
                      : current
                }
              }
            }
          });
          if ([PostEditorContextEnum.edit].includes(postEditorContext.type)) {
            const deleteAlert = !!siblings?.find(
              post => [PostStatus.ACTIVE, PostStatus.DRAFT].includes(post.status) && post.socialProfile.id === profile
            );
            if (deleteAlert) {
              showToast({
                severity: 'warn',
                summary: 'Post(s) will be deleted',
                detail:
                  'When you deselect an active profile the related post will be deleted upon save. This can not be undone.',
                sticky: true
              });
            }
          }
        }
      });
      if (
        profilesIds.length < 2 &&
        [PostEditorContextEnum.new, PostEditorContextEnum.import, PostEditorContextEnum.duplicate].includes(
          postEditorContext.type
        )
      ) {
        // if we have less than 2 profiles, and we are in creation mode we should turn customize off
        result = updateImmutable(result, {
          postGroups: {
            [postGroupIndex]: {
              customizeContentPerProfile: { $set: false },
              customizeSchedulePerProfile: { $set: false },
              posts: {
                $set: []
              }
            }
          }
        });
        result = validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning);
      }
    } else if (profilesIds.length < postsProfileIds.length && profilesIds.length === 0) {
      if ([PostEditorContextEnum.new, PostEditorContextEnum.import, PostEditorContextEnum.duplicate]) {
        // we have clicked to deselect all, so we need to set customize profiles to false
        result = updateImmutable<PostEditorState>(result, {
          postGroups: {
            [postGroupIndex]: {
              customizeContentPerProfile: { $set: false },
              customizeSchedulePerProfile: { $set: false },
              posts: {
                $set: []
              }
            }
          }
        });
        result = validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning);
      } else if ([PostEditorContextEnum.edit].includes(postEditorContext.type)) {
        // we deselected all the profiles, we should activate all the past / failed posts
        // check if we have active / draft siblings and show the warining:
        const deleteAlert = !!siblings?.filter(post => [PostStatus.ACTIVE, PostStatus.DRAFT].includes(post.status))
          .length;
        if (deleteAlert) {
          showToast({
            severity: 'warn',
            summary: 'Post(s) will be deleted',
            detail:
              'When you deselect an active profile the related post will be deleted upon save. This can not be undone.',
            sticky: true
          });
        }
        result = updateImmutable(result, {
          postGroups: {
            [postGroupIndex]: {
              posts: {
                $set: []
              },
              readOnlyPosts: {
                $apply: (current: ReadonlyGroupPostAttributesType[]) => current.map(p => ({ ...p, active: true }))
              },
              errors: {
                $set: InitialPostEditorState().postGroups[0].errors
              }
            }
          }
        });
      }
    }
  }
  return result;
};
